import React, { useState, useEffect } from 'react';
import CarePartnerEmailForm from '../components/CarePartnerEmailForm';
import EditChildren from './EditChildren';
import EditCaregivers from './EditCaregivers';
import ActivityTypeSettings from './ActivityTypeSettings';
import HouseInfo from './HouseInfo';
import RecurringDefaults from '../logistics/RecurringDefaults';
import { applyStandardizeAll } from '../logistics/standardizeAllTitles';
import { publishNowAndCancelBatch } from '../api/schedulePublish';
import { fetchScheduleWindow } from '../api/scheduleSnapshot';
import TabBar from '../components/ui/TabBar';
import AccountMenu from '../components/ui/AccountMenu';
import { useApp } from '../App';
import { fsSet, fsLogChange, fsGetCol, fsDelete } from '../lib/firebase';
import { listCalendars } from '../lib/gcal';
import { renderCoverageMessage, renderCaregiverDeclineMessage } from '../lib/sms';
import { DEFAULT_COVERAGE_MESSAGES } from '../config/appConfig';

// Settings sections are grouped into a handful of top-level groups to keep the
// root menu shallow. Each group with more than one section renders its sections
// as tabs (TabBar); single-section groups render the section directly. Group ids
// are suffixed `_group` so they never collide with a section id of the same name.
const ALL_GROUPS = [
  { id: 'general',          label: 'General',                emoji: '🪨', sections: [{ id: 'general' }] },
  { id: 'family_group',     label: 'Family Setup',           emoji: '🌳', sections: [{ id: 'family' }] },
  { id: 'caregivers_group', label: 'Caregivers & Access',    emoji: '🌾', sections: [
      { id: 'people',     tabLabel: 'Caregivers' },
      { id: 'rates',      tabLabel: 'Rates', requiresPay: true },
      { id: 'messages',   tabLabel: 'Messages' },
      { id: 'activities', tabLabel: 'Activities' },
  ] },
  { id: 'calendars_group',  label: 'Calendars',              emoji: '🌤️', sections: [
      { id: 'calendars',  tabLabel: 'Connections' },
      { id: 'defaults',   tabLabel: 'Recurring Events' },
      { id: 'registries', tabLabel: 'Keywords' },
  ] },
  { id: 'house_group',      label: 'House & Emergency Info', emoji: '🪵', sections: [{ id: 'house' }] },
  { id: 'data_group',       label: 'Data & History',         emoji: '🍂', sections: [
      { id: 'changelog', tabLabel: 'Change Log' },
      { id: 'reset',     tabLabel: 'Reset & Data' },
  ] },
];

// Section id → group id, derived so deep links (initialSection) can't drift.
const SECTION_TO_GROUP = ALL_GROUPS.reduce((acc, g) => {
  for (const s of g.sections) acc[s.id] = g.id;
  return acc;
}, {});

export default function SettingsModule({ onBack, initialSection = null }) {
  const { familyData } = useApp() || {};
  const [group, setGroup] = useState(() => (initialSection ? SECTION_TO_GROUP[initialSection] : null));

  // "General" holds only the (currently feature-flagged-off) cash editor — hide
  // it while that flag is off; it reappears automatically once cash ships.
  const GROUPS = familyData?.config?.features?.cashSignal
    ? ALL_GROUPS
    : ALL_GROUPS.filter(g => g.id !== 'general');

  // Jump to any section by id (used by cross-links like Recurring → Children).
  const navigateToSection = (sectionId) => setGroup(SECTION_TO_GROUP[sectionId]);

  if (group) {
    const groupObj = ALL_GROUPS.find(g => g.id === group);
    if (groupObj) {
      return (
        <SettingsGroup
          key={group}
          group={groupObj}
          initialTab={initialSection}
          onBack={() => setGroup(null)}
          onNavigateToSection={navigateToSection}
        />
      );
    }
  }

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Home</button>
        <h1>Settings</h1>
        <AccountMenu />
      </div>
      <div className="nav-list">
        {GROUPS.map(g => (
          <button key={g.id} className="nav-list-item" onClick={() => setGroup(g.id)}>
            <span className="nav-list-item__emoji">{g.emoji}</span>
            <span className="nav-list-item__label">{g.label}</span>
            <span className="nav-list-item__arrow">›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SettingsGroup({ group, initialTab, onBack, onNavigateToSection }) {
  const { familyData } = useApp() || {};
  const payOn = familyData?.config?.features?.payManagement !== false;
  const sections = group.sections.filter(s => !s.requiresPay || payOn);

  const [tab, setTab] = useState(() =>
    initialTab && sections.some(s => s.id === initialTab) ? initialTab : sections[0].id
  );
  // Snap to the first section if the active tab disappeared (e.g. Rates hidden).
  const active = sections.some(s => s.id === tab) ? tab : sections[0].id;

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Settings</button>
        <h1>{group.label}</h1>
        <AccountMenu />
      </div>
      <div className="settings-body">
        {group.id === 'caregivers_group' && <PayManagementToggle />}
        {sections.length > 1 && (
          <TabBar
            tabs={sections.map(s => ({ id: s.id, label: s.tabLabel }))}
            value={active}
            onChange={setTab}
            style={{ marginBottom: 16 }}
          />
        )}
        <SectionBody id={active} onNavigateToSection={onNavigateToSection} />
      </div>
    </div>
  );
}

function SectionBody({ id, onNavigateToSection }) {
  return (
    <>
      {id === 'general'    && <GeneralSettings />}
      {id === 'people'     && <EditCaregivers />}
      {id === 'rates'      && <RatesSettings />}
      {id === 'messages'   && <CoverageMessages />}
      {id === 'activities' && <ActivityTypeSettings />}
      {id === 'registries' && <KeywordRegistries />}
      {id === 'calendars'  && <CalendarConnections />}
      {id === 'defaults'   && <RecurringDefaults embedded onNavigateToChildren={() => onNavigateToSection?.('family')} />}
      {id === 'family'     && <FamilySetup />}
      {id === 'house'      && <HouseInfo />}
      {id === 'changelog'  && <ChangelogViewer />}
      {id === 'reset'      && <ResetPanel />}
    </>
  );
}

function PayManagementToggle() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const payOn = familyData?.config?.features?.payManagement !== false;
  const [saving, setSaving] = useState(false);

  async function toggle(next) {
    if (!familyData?.id || saving) return;
    setSaving(true);
    await fsSet(`families/${familyData.id}/config/appConfig`, { features: { payManagement: next } });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'toggle_pay_management', toValue: next ? 'on' : 'off',
    });
    setFamilyData(prev => ({
      ...prev,
      config: { ...prev.config, features: { ...prev.config?.features, payManagement: next } },
    }));
    setSaving(false);
  }

  return (
    <SettingRow label="Pay management" description="Track caregiver rates and pay. Off hides the Rates tab.">
      <input type="checkbox" checked={payOn} disabled={saving} onChange={e => toggle(e.target.checked)} />
    </SettingRow>
  );
}

function GeneralSettings() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const [cashOnHand, setCashOnHand] = useState(familyData?.config?.cashOnHand ?? 0);
  const [cashInput, setCashInput]   = useState(String(familyData?.config?.cashOnHand ?? 0));
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [homeItemLimit, setHomeItemLimit] = useState(familyData?.config?.homeItemLimit ?? 5);
  const [homeLimitSaved, setHomeLimitSaved] = useState(false);

  async function saveCash() {
    if (!familyData?.id) return;
    const val = parseFloat(cashInput) || 0;
    setSaving(true);
    await fsSet(`families/${familyData.id}/config/appConfig`, { cashOnHand: val });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'update_cash_on_hand', toValue: String(val),
    });
    setCashOnHand(val);
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, cashOnHand: val } }));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function saveHomeItemLimit(next) {
    if (!familyData?.id) return;
    setHomeItemLimit(next);
    await fsSet(`families/${familyData.id}/config/appConfig`, { homeItemLimit: next });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'update_home_item_limit', toValue: String(next),
    });
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, homeItemLimit: next } }));
    setHomeLimitSaved(true);
    setTimeout(() => setHomeLimitSaved(false), 2000);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Cash on Hand */}
      <div className="cash-setting-block">
        <div className="cash-setting-block__header">
          <span className="cash-setting-block__label">Cash on Hand</span>
          <span className="cash-setting-block__desc">Shown on the home screen. Update whenever you replenish.</span>
        </div>
        <div className="cash-setting-block__row">
          <div className="rate-input">
            <span>$</span>
            <input type="number" className="input input--short" value={cashInput}
              onChange={e => { setCashInput(e.target.value); setSaved(false); }}
              onBlur={() => setCashInput(String(parseFloat(cashInput) || 0))}
              min="0" step="1" />
          </div>
          <button className="btn btn--primary btn--small" onClick={saveCash} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ fontSize: 12, color: 'var(--green)' }}>✓</span>}
        </div>
      </div>

      <SettingRow label="Records shown on Home" description="How many events Today/Coming Up show before '+N more'">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="input input--short" value={homeItemLimit}
            onChange={e => saveHomeItemLimit(+e.target.value)}>
            {[3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {homeLimitSaved && <span style={{ fontSize: 12, color: 'var(--green)' }}>✓</span>}
        </div>
      </SettingRow>
    </div>
  );
}

function RatesSettings() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const saved = familyData?.config?.rates || {};
  const [rates, setRates] = useState({
    twoKids:      saved.twoKids      ?? 25,
    threeKids:    saved.threeKids    ?? 30,
    cashThreshold: saved.cashThreshold ?? 50,
  });
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  async function save() {
    if (!familyData?.id) return;
    setSaving(true);
    await fsSet(`families/${familyData.id}/config/appConfig`, {
      rates,
      cashThreshold: rates.cashThreshold,
    });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'save_rates',
      toValue: JSON.stringify(rates),
    });
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, rates, cashThreshold: rates.cashThreshold } }));
    setSaving(false);
    setSavedOk(true);
  }

  return (
    <div>
      <SettingRow label="Base rate — 1–2 kids" description="Per hour">
        <div className="rate-input">
          <span>$</span>
          <input type="number" className="input input--short" value={rates.twoKids}
            onChange={e => { setRates(r => ({ ...r, twoKids: +e.target.value })); setSavedOk(false); }} />
          <span>/hr</span>
        </div>
      </SettingRow>
      <SettingRow label="Base rate — 3+ kids" description="Per hour">
        <div className="rate-input">
          <span>$</span>
          <input type="number" className="input input--short" value={rates.threeKids}
            onChange={e => { setRates(r => ({ ...r, threeKids: +e.target.value })); setSavedOk(false); }} />
          <span>/hr</span>
        </div>
      </SettingRow>
      <SettingRow label="Rounding" description="Pay rounded up to nearest $5">
        <span className="setting-note">Nearest $5 ↑</span>
      </SettingRow>
      <SettingRow label="Low cash warning" description="Show alert on home screen when cash on hand falls below">
        <div className="rate-input">
          <span>$</span>
          <input type="number" className="input input--short" value={rates.cashThreshold}
            onChange={e => { setRates(r => ({ ...r, cashThreshold: +e.target.value })); setSavedOk(false); }} />
        </div>
      </SettingRow>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
        <button className="btn btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {savedOk && <span style={{ fontSize: 13, color: 'var(--green)' }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// Sample event used to render the live preview in the coverage-message editor.
const COVERAGE_PREVIEW = {
  eventTitle: 'Kilmer pickup',
  description: 'Bring cleats and water bottle',
  start: { dateTime: new Date(Date.now() + 90 * 60000).toISOString() },
};
const COVERAGE_TOKENS = ['{title}', '{time}', '{time_to_event}', '{date}', '{description}'];

// Sample session used to preview the caregiver-decline template — different
// shape from COVERAGE_PREVIEW (a sessions doc, not a GCal event), since
// renderCaregiverDeclineMessage takes different tokens.
const DECLINE_PREVIEW_TOKENS = ['{parents}', '{date}', '{time_range}'];
function declinePreviewSession() {
  const d = new Date(Date.now() + 2 * 86400000);
  return { date: d.toISOString().split('T')[0], startTime: '16:00', endTime: '19:00' };
}

function CoverageMessages() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const saved = familyData?.config?.coverageMessages || {};
  const [msgs, setMsgs] = useState({
    todayLate:        saved.todayLate        ?? DEFAULT_COVERAGE_MESSAGES.todayLate,
    future:           saved.future           ?? DEFAULT_COVERAGE_MESSAGES.future,
    caregiverDecline: saved.caregiverDecline ?? DEFAULT_COVERAGE_MESSAGES.caregiverDecline,
  });
  const [saving, setSaving]   = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  function update(key, value) {
    setMsgs(m => ({ ...m, [key]: value }));
    setSavedOk(false);
  }

  async function save() {
    if (!familyData?.id) return;
    setSaving(true);
    await fsSet(`families/${familyData.id}/config/appConfig`, { coverageMessages: msgs });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'save_coverage_messages',
    });
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, coverageMessages: msgs } }));
    setSaving(false);
    setSavedOk(true);
  }

  const fields = [
    {
      key: 'todayLate', label: 'Running late (today)',
      hint: "Sent from a Today event's handoff button when you're running behind.",
      tokens: COVERAGE_TOKENS,
      preview: text => renderCoverageMessage(text, COVERAGE_PREVIEW),
    },
    {
      key: 'future', label: 'Future request',
      hint: 'Sent from a Coming Up event to ask someone to cover ahead of time.',
      tokens: COVERAGE_TOKENS,
      preview: text => renderCoverageMessage(text, COVERAGE_PREVIEW),
    },
    {
      key: 'caregiverDecline', label: "Caregiver can't make it",
      hint: 'Prefills the text a caregiver sends both parents when they decline a booked session from My Sessions.',
      tokens: DECLINE_PREVIEW_TOKENS,
      preview: text => renderCaregiverDeclineMessage(text, { session: declinePreviewSession(), carePartners: familyData?.carePartners }),
    },
  ];

  return (
    <div>
      <p className="settings-intro">
        These wordings prefill the text message sent for coverage requests and caregiver cancellations. Tap-to-send opens your Messages app — nothing is sent automatically.
      </p>

      {fields.map(f => (
        <div key={f.key} className="registry-section" style={{ marginBottom: 20 }}>
          <h3 className="registry-section__title">{f.label}</h3>
          <p className="registry-section__note" style={{ marginTop: 0 }}>{f.hint}</p>
          <div className="registry-keywords" style={{ marginBottom: 8 }}>
            {f.tokens.map(t => <span key={t} className="kw-chip kw-chip--custom">{t}</span>)}
          </div>
          <textarea
            className="input input--textarea"
            rows={3}
            value={msgs[f.key]}
            onChange={e => update(f.key, e.target.value)}
          />
          <p className="coverage-preview">
            <span className="coverage-preview__eyebrow">Preview</span>
            {f.preview(msgs[f.key])}
          </p>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {savedOk && <span style={{ fontSize: 13, color: 'var(--green)' }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// CarePartnerProfiles moved into EditCarePartners inline edit form
const DEFAULT_REGISTRIES = [
  { id: 'workModes',     label: 'Work Modes',                  defaults: ['Work', 'Office', 'Clinical', 'Clinic', 'Conference', 'Travel'] },
  { id: 'auxiliaryCare',label: 'Auxiliary / Backup Care',      defaults: ['Bright Horizons', 'Back-Up Care', 'AKP', 'Au Pair', 'Grampy', 'Deedle'] },
  { id: 'schoolSignals', label: 'School / Schedule Signals',   defaults: ['No School', 'Day Off', 'Holiday', 'Early Release', 'Half Day'] },
  { id: 'reminderSignals', label: 'Reminder Signals',          defaults: ['reminder: yes'] },
];

function KeywordRegistries() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const savedRegs = familyData?.config?.keywordRegistries || {};

  // State: { [regId]: { disabled: Set<string>, custom: string[] } }
  const [regs, setRegs] = useState(() => {
    const init = {};
    for (const r of DEFAULT_REGISTRIES) {
      init[r.id] = {
        disabled: new Set(savedRegs[r.id + '_disabled'] || []),
        custom:   savedRegs[r.id + '_custom'] || [],
      };
    }
    return init;
  });
  const [adding, setAdding] = useState(null); // regId | null
  const [newKw, setNewKw]   = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  function toggleDefault(regId, kw) {
    setRegs(prev => {
      const disabled = new Set(prev[regId].disabled);
      disabled.has(kw) ? disabled.delete(kw) : disabled.add(kw);
      return { ...prev, [regId]: { ...prev[regId], disabled } };
    });
    setSaved(false);
  }

  function addCustom(regId) {
    if (!newKw.trim()) return;
    setRegs(prev => ({
      ...prev,
      [regId]: { ...prev[regId], custom: [...prev[regId].custom, newKw.trim()] },
    }));
    setNewKw('');
    setAdding(null);
    setSaved(false);
  }

  function removeCustom(regId, kw) {
    setRegs(prev => ({
      ...prev,
      [regId]: { ...prev[regId], custom: prev[regId].custom.filter(k => k !== kw) },
    }));
    setSaved(false);
  }

  async function save() {
    if (!familyData?.id) return;
    setSaving(true);
    const payload = {};
    for (const r of DEFAULT_REGISTRIES) {
      payload[r.id]              = [...r.defaults.filter(k => !regs[r.id].disabled.has(k)), ...regs[r.id].custom];
      payload[r.id + '_disabled'] = [...regs[r.id].disabled];
      payload[r.id + '_custom']   = regs[r.id].custom;
    }
    await fsSet(`families/${familyData.id}/config/appConfig`, { keywordRegistries: payload });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'save_keyword_registries',
    });
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, keywordRegistries: payload } }));
    setSaving(false);
    setSaved(true);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {DEFAULT_REGISTRIES.map(reg => (
        <div key={reg.id} className="registry-section">
          <h3 className="registry-section__title">{reg.label}</h3>
          <p className="registry-section__note">Built-in keywords — toggle off to disable</p>
          <div className="registry-keywords">
            {reg.defaults.map(kw => {
              const on = !regs[reg.id].disabled.has(kw);
              return (
                <button key={kw}
                  className={`kw-chip ${on ? 'kw-chip--on' : 'kw-chip--off'}`}
                  onClick={() => toggleDefault(reg.id, kw)}
                  title={on ? 'Click to disable' : 'Click to enable'}>
                  {kw}{on ? '' : ' ✕'}
                </button>
              );
            })}
          </div>
          {regs[reg.id].custom.length > 0 && (
            <>
              <p className="registry-section__note" style={{ marginTop: 8 }}>Custom</p>
              <div className="registry-keywords">
                {regs[reg.id].custom.map(kw => (
                  <span key={kw} className="kw-chip kw-chip--custom">
                    {kw}
                    <button className="kw-chip__remove" onClick={() => removeCustom(reg.id, kw)}>✕</button>
                  </span>
                ))}
              </div>
            </>
          )}
          {adding === reg.id ? (
            <div className="registry-add-row">
              <input className="input input--sm" placeholder="New keyword" value={newKw} autoFocus
                onChange={e => setNewKw(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustom(reg.id)} />
              <button className="btn btn--primary btn--small" onClick={() => addCustom(reg.id)}
                disabled={!newKw.trim()}>Add</button>
              <button className="btn btn--ghost btn--small" onClick={() => { setAdding(null); setNewKw(''); }}>Cancel</button>
            </div>
          ) : (
            <button className="btn btn--ghost btn--small" style={{ marginTop: 8 }}
              onClick={() => { setAdding(reg.id); setNewKw(''); }}>+ Add keyword</button>
          )}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: 13, color: 'var(--green)' }}>✓ Saved</span>}
      </div>
    </div>
  );
}

function ChangelogViewer() {
  const { familyData } = useApp() || {};
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(30);

  useEffect(() => {
    if (!familyData?.id) return;
    load();
  }, [familyData?.id]);

  async function load() {
    setLoading(true);
    try {
      const rows = await fsGetCol(`families/${familyData.id}/changelog`);
      // Sort newest-first by timestamp
      const sorted = (rows || []).sort((a, b) => {
        const at = a.timestamp?.seconds || 0;
        const bt = b.timestamp?.seconds || 0;
        return bt - at;
      });
      setEntries(sorted);
    } catch (err) {
      console.error('[Changelog]', err);
    } finally {
      setLoading(false);
    }
  }

  function fmtTime(ts) {
    if (!ts) return '';
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function fmtAction(entry) {
    const map = {
      assign_event:        'Assigned event',
      exempt_event:        'Exempted event',      // legacy — historical changelog entries only
      unexempt_event:      'Un-exempted event',   // legacy — historical changelog entries only
      reminder_event:      'Marked as reminder',
      unreminder_event:    'Removed reminder',
      lock_in_review:      'Locked in review',
      save_calendar_assignments: 'Saved calendar assignments',
      invite_care_partner: 'Invited care partner',
      edit_care_partner:   'Edited care partner',
      remove_care_partner: 'Removed care partner',
      add_child:           'Added child',
      edit_child:          'Edited child',
      remove_child:        'Removed child',
      add_sitter:          'Added caregiver',
      edit_sitter:         'Edited caregiver',
      save_rates:          'Saved caregiver rates',
      save_house_info:     'Saved house info',
      toggle_pay_management: 'Toggled pay management',
      save_coverage_messages: 'Saved coverage messages',
    };
    if (map[entry.action]) return map[entry.action];
    if (!entry.action) return 'Action';
    // Fallback for any action not in the map above — turn a raw code
    // flag like "override_pickup" into "Override Pickup" instead of
    // leaking the snake_case string into the UI.
    return entry.action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  function fmtActor(entry) {
    if (entry.actorName) return entry.actorName.trim().split(/\s+/)[0];
    if (entry.actorId) return entry.actorId.split('@')[0];
    return '—';
  }

  const visible = entries.slice(0, limit);

  return (
    <div>
      <p className="settings-intro settings-intro--strong">All saved changes and key app actions are logged here.</p>
      {loading && <p className="placeholder-text">Loading…</p>}
      {!loading && entries.length === 0 && (
        <p className="empty-state">No entries yet — actions like Lock In, editing family members, and saving settings will appear here.</p>
      )}
      {!loading && entries.length > 0 && (
        <>
          <div className="changelog-list">
            {visible.map((e, i) => (
              <div key={e.id || i} className="changelog-card">
                <div className="changelog-card__top">
                  <span className="changelog-card__action">{fmtAction(e)}</span>
                  <span className="changelog-card__time">{fmtTime(e.timestamp)}</span>
                </div>
                {e.entityType === 'event' && e.toValue && (
                  <div className="changelog-card__detail">{e.toValue}</div>
                )}
                <div className="changelog-card__by">{fmtActor(e)}</div>
              </div>
            ))}
          </div>
          {entries.length > limit && (
            <button className="btn btn--ghost btn--small" style={{ marginTop: 8 }}
              onClick={() => setLimit(l => l + 30)}>
              Load {Math.min(30, entries.length - limit)} more
            </button>
          )}
        </>
      )}
      <button className="btn btn--ghost btn--small" style={{ marginTop: 12 }} onClick={load}>↻ Refresh</button>
    </div>
  );
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────

function SettingRow({ label, description, children, card = false }) {
  return (
    <div className={`setting-row${card ? ' setting-row--card' : ''}`}>
      <div className="setting-row__text">
        <span className="setting-row__label">{label}</span>
        {description && <span className="setting-row__desc">{description}</span>}
      </div>
      <div className="setting-row__control">{children}</div>
    </div>
  );
}

// ─── RESET PANEL ─────────────────────────────────────────────────────────────

const SUBSET_OPTIONS = [
  { id: 'children',     label: 'Children',                  desc: 'All child profiles' },
  { id: 'care_partners',label: 'Care Partners',             desc: 'Co-managers (you remain as creator)' },
  { id: 'calendars',    label: 'Calendar Connections',      desc: 'Calendar links and assignments' },
  { id: 'config',       label: 'Settings & Configuration',  desc: 'Rates, keywords, rules, notification prefs' },
  { id: 'sessions',     label: 'Session History',           desc: 'Completed caregiver sessions and pay records' },
];

// ─── FAMILY SETUP ─────────────────────────────────────────────────────────────

function FamilySetup() {
  const { familyData } = useApp() || {};
  const [panel, setPanel] = useState(null); // null | 'name' | 'cp' | 'children'

  if (panel === 'name')     return <EditFamilyName     onBack={() => setPanel(null)} />;
  if (panel === 'cp')       return <EditCarePartners   onBack={() => setPanel(null)} />;
  if (panel === 'children') return <EditChildren       onBack={() => setPanel(null)} />;

  const carePartners = familyData?.carePartners || [];
  const children     = familyData?.children     || [];

  return (
    <div className="family-setup-list">
      <FamilySetupRow
        label="Family Name"
        value={familyData?.familyName || '—'}
        onEdit={() => setPanel('name')}
      />
      <FamilySetupRow
        label="Care Partners"
        value={carePartners.map(cp => cp.name || cp.firstName).join(', ') || 'None'}
        detail={`${carePartners.length} partner${carePartners.length !== 1 ? 's' : ''}`}
        onEdit={() => setPanel('cp')}
      />
      <FamilySetupRow
        label="Children"
        value={children.map(c => c.firstName).join(', ') || 'None'}
        detail={`${children.length} child${children.length !== 1 ? 'ren' : ''}`}
        onEdit={() => setPanel('children')}
      />
    </div>
  );
}

function FamilySetupRow({ label, value, detail, onEdit }) {
  return (
    <SettingRow card label={label} description={detail ? `${value} · ${detail}` : value}>
      <button className="btn btn--ghost btn--small" onClick={onEdit}>Edit</button>
    </SettingRow>
  );
}

// ── Edit Family Name ──────────────────────────────────────────────────────────

function EditFamilyName({ onBack }) {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const [name, setName] = useState(familyData?.familyName || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim() || !familyData?.id) return;
    setSaving(true);
    await fsSet(`families/${familyData.id}`, { familyName: name.trim() });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'edit_family_name', toValue: name.trim(),
    });
    setFamilyData(prev => ({ ...prev, familyName: name.trim() }));
    setSaving(false);
    onBack();
  }

  return (
    <div className="family-edit-panel">
      <button className="btn btn--back-sm" onClick={onBack}>← Back</button>
      <h3>Family Name</h3>
      <input className="input" value={name} onChange={e => setName(e.target.value)}
        placeholder="e.g. The McDonalds" autoFocus />
      <button className="btn btn--primary" onClick={save} disabled={saving || !name.trim()}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

// ── Edit Care Partners ────────────────────────────────────────────────────────

function EditCarePartners({ onBack }) {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const [partners, setPartners]     = useState(familyData?.carePartners || []);
  const [adding, setAdding]         = useState(false);
  const [editingId, setEditingId]   = useState(null); // cp.id being inline-edited
  const [editForm, setEditForm]     = useState({});
  const [savingId, setSavingId]     = useState(null);

  const existingEmails = partners.map(cp => cp.email).filter(Boolean);

  async function handleAdd(cpData) {
    if (!familyData?.id) return;
    const id = `cp_${cpData.email.split('@')[0]}`;
    const cp = { id, ...cpData, isCreator: false };

    await fsSet(`families/${familyData.id}/carePartners/${id}`, cp);

    const currentAllowed = familyData.allowedEmails || [];
    if (!currentAllowed.includes(cpData.email)) {
      await fsSet(`families/${familyData.id}`,
        { allowedEmails: [...currentAllowed, cpData.email] });
    }

    await fsSet(`pendingInvites/${id}`, {
      familyId: familyData.id,
      familyName: familyData.familyName,
      inviterName: currentUser?.name || 'Your family',
      email: cpData.email,
      createdAt: new Date().toISOString(),
    });

    const updated = [...partners, cp];
    setPartners(updated);
    setFamilyData(prev => ({ ...prev, carePartners: updated }));
    setAdding(false);
  }

  function startEdit(cp) {
    setEditingId(cp.id);
    setEditForm({ name: cp.name || '', shortName: cp.shortName || '', identifier: cp.identifier || '', phone: cp.phone || '', employer: cp.employer || '', convention: cp.convention || 'standing_window', windowStart: cp.windowStart || '08:30', windowEnd: cp.windowEnd || '16:30' });
  }

  async function saveEdit(cp) {
    if (!familyData?.id) return;
    setSavingId(cp.id);
    const updates = {
      name:        editForm.name.trim()      || cp.name,
      shortName:   editForm.shortName.trim() || '',
      identifier:  editForm.identifier.trim().toUpperCase().replace(/[^A-Z\-\/]/g, '').slice(0, 6) || cp.identifier,
      phone:       editForm.phone.trim(),
      employer:    editForm.employer.trim(),
      convention:  editForm.convention,
      windowStart: editForm.windowStart,
      windowEnd:   editForm.windowEnd,
    };
    await fsSet(`families/${familyData.id}/carePartners/${cp.id}`, updates);
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'edit_care_partner', entityId: cp.id,
      toValue: `${updates.name} (${updates.identifier})`,
    });
    const updated = partners.map(p => p.id === cp.id ? { ...p, ...updates } : p);
    setPartners(updated);
    setFamilyData(prev => ({ ...prev, carePartners: updated }));
    setEditingId(null);
    setSavingId(null);
  }

  async function removePartner(cp) {
    if (cp.isCreator) return;
    if (!window.confirm(`Remove ${cp.name}? They will lose access to the family.`)) return;
    await fsDelete(`families/${familyData.id}/carePartners/${cp.id}`);
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'remove_care_partner', entityId: cp.id,
      toValue: cp.name || cp.email,
    });
    const updated = partners.filter(p => p.id !== cp.id);
    setPartners(updated);
    setFamilyData(prev => ({ ...prev, carePartners: updated }));
  }

  return (
    <div className="family-edit-panel">
      <button className="btn btn--back-sm" onClick={onBack}>← Back</button>
      <h3>Care Partners</h3>

      <div className="edit-list">
        {partners.map(cp => {
          const isEditing = editingId === cp.id;
          return (
            <div key={cp.id} className={`edit-list-row edit-list-row--cp ${isEditing ? 'edit-list-row--editing' : ''}`}>

              <div style={{ flex: 1, minWidth: 0 }}>
                {!isEditing ? (
                  /* ── Read view ── */
                  <>
                    <div className="edit-list-row__name">
                      {cp.shortName ? `${cp.shortName} (${cp.name})` : cp.name || cp.email}
                      {cp.isCreator && <span className="badge badge--blue">You</span>}
                      <span className="cp-id-chip">({cp.identifier})</span>
                    </div>
                    <div className="edit-list-row__sub">{[cp.email, cp.phone].filter(Boolean).join(' · ')}</div>
                  </>
                ) : (
                  /* ── Edit form ── */
                  <div className="cp-edit-form">
                    <div className="cp-edit-form__row">
                      <div className="cp-edit-form__field">
                        <label className="cp-edit-form__label">Full name</label>
                        <input className="input input--sm" value={editForm.name}
                          onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} autoFocus />
                      </div>
                      <div className="cp-edit-form__field cp-edit-form__field--narrow">
                        <label className="cp-edit-form__label">Short name <span className="cp-edit-form__hint">(optional)</span></label>
                        <input className="input input--sm" value={editForm.shortName} placeholder="e.g. Katy"
                          onChange={e => setEditForm(f => ({ ...f, shortName: e.target.value }))} />
                      </div>
                      <div className="cp-edit-form__field cp-edit-form__field--id">
                        <label className="cp-edit-form__label">Calendar ID</label>
                        <input className="input input--sm input--id" value={editForm.identifier}
                          maxLength={6} placeholder="A"
                          onChange={e => setEditForm(f => ({ ...f, identifier: e.target.value.toUpperCase().replace(/[^A-Z\-\/]/g, '') }))} />
                      </div>
                    </div>
                    <p className="cp-edit-form__note">ID appears on calendar events as ({editForm.identifier || '?'}). Email cannot be changed — remove and re-add to change email.</p>
                    <div className="cp-edit-form__field">
                      <label className="cp-edit-form__label">Phone <span className="cp-edit-form__hint">(optional — lets you text them to request coverage)</span></label>
                      <input className="input input--sm" type="tel" value={editForm.phone} placeholder="(555) 123-4567"
                        onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                    </div>
                    <div className="cp-edit-form__field">
                      <label className="cp-edit-form__label">Employer <span className="cp-edit-form__hint">(optional — short nickname used in travel event titles, e.g. C1)</span></label>
                      <input className="input input--sm" value={editForm.employer} placeholder="e.g. C1"
                        onChange={e => setEditForm(f => ({ ...f, employer: e.target.value }))} />
                    </div>
                    <div className="cp-edit-form__field" style={{ marginTop: 8 }}>
                      <label className="cp-edit-form__label">Schedule convention</label>
                      <select className="input input--sm" value={editForm.convention}
                        onChange={e => setEditForm(f => ({ ...f, convention: e.target.value }))}>
                        <option value="standing_window">Never scheduled — assume unavailable during standard hours</option>
                        <option value="always_scheduled">Always scheduled — calendar is source of truth</option>
                        <option value="mixed">Mixed — default window + calendar overrides</option>
                      </select>
                    </div>
                    {(editForm.convention === 'standing_window' || editForm.convention === 'mixed') && (
                      <div className="cp-edit-form__field">
                        <label className="cp-edit-form__label">Unavailable window</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="time" className="input input--sm" style={{ width: 120 }}
                            value={editForm.windowStart}
                            onChange={e => setEditForm(f => ({ ...f, windowStart: e.target.value }))} />
                          <span style={{ fontSize: 12, color: 'var(--gray)' }}>to</span>
                          <input type="time" className="input input--sm" style={{ width: 120 }}
                            value={editForm.windowEnd}
                            onChange={e => setEditForm(f => ({ ...f, windowEnd: e.target.value }))} />
                        </div>
                      </div>
                    )}
                    <div className="cp-edit-form__actions">
                      <button className="btn btn--primary btn--small"
                        onClick={() => saveEdit(cp)} disabled={savingId === cp.id}>
                        {savingId === cp.id ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn--ghost btn--small"
                        onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Row actions — hidden while editing */}
              {!isEditing && (
                <div className="cp-row-actions">
                  <button className="cp-action-btn" onClick={() => startEdit(cp)}
                    title="Edit name / ID">✏️</button>
                  {!cp.isCreator && (
                    <button className="cp-action-btn cp-action-btn--danger"
                      onClick={() => removePartner(cp)}
                      title="Remove care partner">✕</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {adding ? (
        <CarePartnerEmailForm
          onAdd={handleAdd}
          onCancel={() => setAdding(false)}
          existingEmails={existingEmails}
        />
      ) : (
        <button className="btn btn--secondary" style={{ marginTop: 12 }} onClick={() => setAdding(true)}>
          + Add Care Partner
        </button>
      )}
    </div>
  );
}

function CalendarConnections() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const [gcalCals, setGcalCals]     = useState([]);   // from Google
  const [manualCals, setManualCals] = useState([]);   // iCal/URI entries
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);
  const [addingManual, setAddingManual] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', uri: '', personName: '' });

  const appConfig      = familyData?.config || {};
  const calAssignments = appConfig.calendarAssignments || {};
  const suppressedCals = appConfig.suppressedCalendars || [];
  const calMeta        = appConfig.calendarMeta || [];
  const savedManual    = appConfig.manualCalendars || [];

  const carePartners = familyData?.carePartners || [];
  const children     = familyData?.children     || [];
  // Person options: each CP/child name + Family (shared) + Ignore
  const personOptions = [
    ...carePartners.map(cp => cp.name || cp.firstName),
    ...children.map(c => c.firstName),
    'Family',
    'Ignore (suppress)',
  ];

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const cals = await listCalendars(); // returns a bare array of calendar entries
        const mapped = cals.map(cal => {
          const meta = calMeta.find(m => m.id === cal.id);
          const saved = calAssignments[cal.id] || meta?.personName || '';
          return {
            ...cal,
            personName: saved || (cal.primary ? carePartners[0]?.name || '' : ''),
            suppress: suppressedCals.includes(cal.id),
          };
        });
        setGcalCals(mapped);
        setManualCals(savedManual.map(m => ({ ...m, suppress: suppressedCals.includes(m.id) })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function assignGcal(calId, val) {
    setGcalCals(prev => prev.map(c =>
      c.id === calId ? { ...c, personName: val, suppress: val === 'Ignore (suppress)' } : c
    ));
    setSaved(false);
  }

  function assignManual(calId, val) {
    setManualCals(prev => prev.map(c =>
      c.id === calId ? { ...c, personName: val, suppress: val === 'Ignore (suppress)' } : c
    ));
    setSaved(false);
  }

  function addManualCal() {
    if (!manualForm.name.trim() || !manualForm.uri.trim()) return;
    const id = `manual_${Date.now()}`;
    setManualCals(prev => [...prev, { id, name: manualForm.name.trim(), uri: manualForm.uri.trim(), personName: manualForm.personName, suppress: false }]);
    setManualForm({ name: '', uri: '', personName: '' });
    setAddingManual(false);
    setSaved(false);
  }

  function removeManualCal(calId) {
    setManualCals(prev => prev.filter(c => c.id !== calId));
    setSaved(false);
  }

  async function saveAssignments() {
    if (!familyData?.id) return;
    setSaving(true);
    const newAssignments = {};
    const newSuppressed  = [];
    const allCals = [...gcalCals, ...manualCals];
    for (const cal of allCals) {
      if (cal.suppress || cal.personName === 'Ignore (suppress)') {
        newSuppressed.push(cal.id);
      } else if (cal.personName) {
        newAssignments[cal.id] = cal.personName;
      }
    }
    await fsSet(`families/${familyData.id}/config/appConfig`, {
      calendarAssignments: newAssignments,
      suppressedCalendars: newSuppressed,
      manualCalendars: manualCals.map(c => ({ id: c.id, name: c.name, uri: c.uri, personName: c.personName })),
      calendarMeta: gcalCals.map(c => ({ id: c.id, name: c.summary, color: c.backgroundColor, personName: c.personName, accessRole: c.accessRole || null })),
    });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'save_calendar_assignments',
      toValue: JSON.stringify(newAssignments),
    });
    setFamilyData(prev => ({
      ...prev,
      config: { ...prev.config, calendarAssignments: newAssignments, suppressedCalendars: newSuppressed, manualCalendars: manualCals },
    }));
    setSaving(false);
    setSaved(true);
  }

  if (loading) return <p className="placeholder-text">Discovering calendars…</p>;
  if (error)   return <div className="error-banner" style={{ borderRadius: 8 }}>🌧️ {error}</div>;

  const CalRow = ({ cal, onAssign, onRemove }) => {
    const nameLine = cal.summary || cal.name || '(Unnamed)';
    const idLine    = cal.uri || cal.id;
    // A primary Google calendar's `id` (and often `summary`) IS the account's
    // own email — showing it again underneath just repeats the line above,
    // wrapped once and truncated once. Only show the id line when it's
    // actually a second, distinct piece of information.
    const showId = idLine && idLine !== nameLine;
    return (
      <div className="cal-assignment-row">
        <div className="cal-assignment-row__left">
          <span className="cal-dot" style={{ background: cal.backgroundColor || cal.color || '#4285F4' }} />
          <div className="cal-assignment-row__names">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className={`cal-assignment-row__name${showId ? '' : ' cal-assignment-row__name--solo'}`}>{nameLine}</span>
              {cal.primary && <span className="badge badge--blue">Primary</span>}
              {cal.uri && <span className="badge badge--gray">iCal</span>}
            </div>
            {showId && <span className="cal-assignment-row__id">{idLine}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select
            className="input input--select cal-assignment-row__select"
            value={cal.suppress ? 'Ignore (suppress)' : (cal.personName || '')}
            onChange={e => onAssign(cal.id, e.target.value)}
          >
            <option value="">— unassigned —</option>
            {personOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {onRemove && (
            <button className="btn btn--ghost btn--small btn--danger-ghost" onClick={() => onRemove(cal.id)} title="Remove">✕</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 16 }}>
        Assign each calendar to a family member. Use <strong>Family</strong> for shared calendars that don't belong to one person. Grove uses this to detect coverage gaps.
      </p>

      <div className="cal-section-label">Google Calendars</div>
      <div className="cal-assignment-list">
        {gcalCals.map(cal => (
          <CalRow key={cal.id} cal={cal} onAssign={assignGcal} />
        ))}
      </div>

      <div className="cal-section-label" style={{ marginTop: 20 }}>
        Additional Calendars
        <span className="cal-section-label__sub">iCal links or calendar URIs</span>
      </div>
      <div className="cal-assignment-list">
        {manualCals.length === 0 && !addingManual && (
          <p style={{ fontSize: 12, color: 'var(--gray)', padding: '8px 0' }}>None added yet</p>
        )}
        {manualCals.map(cal => (
          <CalRow key={cal.id} cal={cal} onAssign={assignManual} onRemove={removeManualCal} />
        ))}
      </div>

      {addingManual ? (
        <div className="add-form" style={{ marginTop: 8 }}>
          <input className="input" placeholder="Calendar name (e.g. School Calendar)"
            value={manualForm.name} onChange={e => setManualForm(f => ({ ...f, name: e.target.value }))} autoFocus />
          <input className="input" placeholder="iCal URL or webcal:// URI"
            value={manualForm.uri} onChange={e => setManualForm(f => ({ ...f, uri: e.target.value }))} />
          <select className="input input--select" value={manualForm.personName}
            onChange={e => setManualForm(f => ({ ...f, personName: e.target.value }))}>
            <option value="">— assign to —</option>
            {personOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="add-form__actions">
            <button className="btn btn--primary btn--small" onClick={addManualCal}
              disabled={!manualForm.name.trim() || !manualForm.uri.trim()}>Add</button>
            <button className="btn btn--ghost btn--small" onClick={() => setAddingManual(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn--secondary btn--small" style={{ marginTop: 8 }}
          onClick={() => setAddingManual(true)}>+ Add iCal / calendar URI</button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
        <button className="btn btn--primary" onClick={saveAssignments} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: 13, color: 'var(--green)' }}>✓ Saved</span>}
      </div>
    </div>
  );
}

function ResetPanel() {
  const useAppCtx = useApp() || {};
  const [mode, setMode] = useState(null); // null | 'nuclear' | 'subset'
  const [subsetSelected, setSubsetSelected] = useState([]);
  const [confirm1, setConfirm1] = useState(false);
  const [confirm2, setConfirm2] = useState(false);
  const [nuclearText, setNuclearText] = useState('');
  const [done, setDone] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateMsg, setMigrateMsg] = useState(null);
  const [standardizing, setStandardizing] = useState(false);
  const [standardizeMsg, setStandardizeMsg] = useState(null);
  const [migratingEmails, setMigratingEmails] = useState(false);
  const [migrateEmailsMsg, setMigrateEmailsMsg] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const [publishErr, setPublishErr] = useState(null);

  // Manual override for the batched auto-publish (api/schedulePublish.js) —
  // that now handles caregiver freshness in the background within ~90s of
  // any schedule-touching action, so this is just "don't want to wait."
  // Moved here from Review & Assign, which no longer surfaces it inline.
  async function handlePublishNow() {
    const { familyData } = useAppCtx;
    if (!familyData?.id) return;
    setPublishing(true);
    setPublishErr(null);
    try {
      const days = await fetchScheduleWindow(familyData);
      await publishNowAndCancelBatch(familyData.id, days, familyData?.children || [], { pickupsActive: familyData?.config?.pickupsActive !== false });
      setPublishedAt(new Date());
    } catch (err) {
      setPublishErr('Publish failed: ' + err.message);
    }
    setPublishing(false);
  }

  // One-time, idempotent: the old guest/sitter email invite lists predate
  // per-caregiver access. Google sign-in now resolves through a caregiver's
  // own record (families/{id}/sitters/{id}.access), so each legacy email
  // gets linked to a matching caregiver — or a placeholder one is created —
  // then the legacy fields are cleared. Safe to run more than once.
  async function migrateSignInEmails() {
    const { familyData, setFamilyData, currentUser } = useAppCtx;
    if (!familyData?.id) return;
    const legacyGuests  = familyData.guestEmails  || [];
    const legacySitters = familyData.sitterEmails || [];
    if (!legacyGuests.length && !legacySitters.length) {
      setMigrateEmailsMsg('✓ Nothing to migrate — no legacy guest/sitter emails found');
      setTimeout(() => setMigrateEmailsMsg(null), 6000);
      return;
    }
    setMigratingEmails(true);
    try {
      let sitters = [...(familyData.sitters || [])];
      let created = 0, linked = 0;
      const entries = [
        ...legacyGuests.map(email => ({ email, scopes: { schedule: true, kids: true, house: true, emergency: false, sessions: false } })),
        ...legacySitters.map(email => ({ email, scopes: { schedule: true, kids: true, house: true, emergency: true, sessions: true } })),
      ];
      for (const { email, scopes } of entries) {
        const clean = email.trim().toLowerCase();
        if (!clean) continue;
        let sitter = sitters.find(s => (s.email || '').toLowerCase() === clean);
        if (sitter) {
          sitter = { ...sitter, access: { enabled: true, scopes: sitter.access?.scopes || scopes, expiresDays: sitter.access?.expiresDays ?? '90', googleSignInEnabled: true } };
          linked++;
        } else {
          const id = `sitter_${Date.now()}_${created}`;
          sitter = {
            id, firstName: clean.split('@')[0], lastName: '', phone: '', email: clean,
            identifier: clean[0]?.toUpperCase() || 'C', sitterType: 'standard', caregiverType: 'sitter',
            pickupEligible: false, rateOverride: null, blackoutDays: [], active: true,
            access: { enabled: true, scopes, expiresDays: '90', googleSignInEnabled: true },
          };
          created++;
        }
        await fsSet(`families/${familyData.id}/sitters/${sitter.id}`, sitter);
        sitters = sitters.some(s => s.id === sitter.id) ? sitters.map(s => s.id === sitter.id ? sitter : s) : [...sitters, sitter];
      }
      const signInEmails = Array.from(new Set([...(familyData.signInEmails || []), ...entries.map(e => e.email.trim().toLowerCase())]));
      await fsSet(`families/${familyData.id}`, { signInEmails, guestEmails: [], sitterEmails: [] });
      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'settings', action: 'migrate_signin_emails',
        toValue: `${linked} linked, ${created} placeholder caregiver(s) created`,
      });
      setFamilyData(prev => ({ ...prev, sitters, signInEmails, guestEmails: [], sitterEmails: [] }));
      setMigrateEmailsMsg(`✓ Migrated ${entries.length} email(s) — ${linked} linked to existing caregivers, ${created} new profile(s) created. Review them in Settings → Caregivers.`);
    } catch (err) {
      setMigrateEmailsMsg('Migration failed: ' + err.message);
    }
    setMigratingEmails(false);
    setTimeout(() => setMigrateEmailsMsg(null), 10000);
  }

  // One-off, full-history version of Recurring Events' "Standardize titles" —
  // that screen only ever sees a 60-day window, so this reaches everything.
  async function standardizeAllTitles() {
    const { familyData, currentUser } = useAppCtx;
    if (!familyData?.id) return;
    setStandardizing(true);
    setStandardizeMsg('Scanning recurring events…');
    try {
      const results = await applyStandardizeAll(familyData, {
        onProgress: (done, total) => setStandardizeMsg(`Processing ${done}/${total}…`),
      });
      const updated = results.filter(r => r.status === 'updated').length;
      const failed  = results.filter(r => r.status === 'failed').length;
      if (updated > 0) {
        await fsLogChange(familyData.id, {
          actorId: currentUser?.email, actorName: currentUser?.name,
          module: 'settings', action: 'standardize_all_titles',
          toValue: `${updated} title(s) cleaned up${failed ? `, ${failed} failed` : ''}`,
        });
      }
      setStandardizeMsg(
        results.length === 0
          ? '✓ Nothing to standardize — all titles already match the format'
          : `✓ ${updated} title(s) updated${failed ? `, ${failed} failed` : ''}`
      );
    } catch (err) {
      setStandardizeMsg('Standardize failed: ' + err.message);
    }
    setStandardizing(false);
    setTimeout(() => setStandardizeMsg(null), 8000);
  }

  // One-time, idempotent: move alarm/gate codes and kid insurance ids from
  // publicly-readable docs into the owner-only private/ subcollection.
  async function migrateSensitiveFields() {
    const { familyData, setFamilyData, currentUser } = useAppCtx;
    if (!familyData?.id) return;
    setMigrating(true);
    try {
      const { loadHouseData, saveSafetyInfo, saveSecurity, saveInsurance } = await import('../lib/houseData');
      const houseData = await loadHouseData(familyData.id, { includePrivate: true });
      let moved = 0;

      // 1. alarm/gate codes: safetyInfo → private/security
      const safety = houseData.safetyInfo || {};
      const SECURITY_KEYS = ['alarm_code', 'alarm_company_name', 'alarm_company_phone', 'garage_gate_code'];
      const secret = { ...(houseData.security || {}) };
      const cleaned = { ...safety };
      let safetyDirty = false;
      for (const k of SECURITY_KEYS) {
        if (safety[k]) {
          if (!secret[k]) secret[k] = safety[k];
          cleaned[k] = '';
          safetyDirty = true;
          moved++;
        }
      }
      if (safetyDirty) {
        await saveSecurity(familyData.id, secret);
        await saveSafetyInfo(familyData.id, cleaned);
      }

      // 2. insurance ids: children/{id} → private/insurance
      const insurance = { ...(houseData.insurance || {}) };
      const updatedChildren = [];
      for (const child of (familyData.children || [])) {
        if (child.insurance_provider || child.insurance_member_id) {
          if (!insurance[child.id]?.provider) {
            insurance[child.id] = {
              provider: child.insurance_provider || '',
              memberId: child.insurance_member_id || '',
            };
          }
          const scrubbed = { ...child, insurance_provider: '', insurance_member_id: '' };
          await fsSet(`families/${familyData.id}/children/${child.id}`, scrubbed);
          updatedChildren.push(scrubbed);
          moved++;
        } else {
          updatedChildren.push(child);
        }
      }
      if (Object.keys(insurance).length) await saveInsurance(familyData.id, insurance);

      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'settings', action: 'migrate_sensitive_fields',
        entityType: 'family', entityId: familyData.id,
        toValue: `${moved} field(s) moved to private/`,
      });

      setFamilyData(prev => ({
        ...prev,
        children: updatedChildren,
        houseData: { ...(prev.houseData || {}), safetyInfo: cleaned, security: secret, insurance },
      }));
      setMigrateMsg(moved > 0 ? `✓ Moved ${moved} sensitive field(s) to private storage` : '✓ Nothing to move — already migrated');
    } catch (err) {
      setMigrateMsg('Migration failed: ' + err.message);
    }
    setMigrating(false);
    setTimeout(() => setMigrateMsg(null), 6000);
  }

  function toggleSubset(id) {
    setSubsetSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function executeNuclear() {
    const { familyData, handleSignOut } = useAppCtx;
    if (familyData?.id) {
      // Delete all subcollections then the family doc
      const subs = ['carePartners', 'children', 'sitters', 'changelog', 'sessions', 'schedule'];
      for (const sub of subs) {
        const docs = await fsGetCol(`families/${familyData.id}/${sub}`);
        for (const d of (docs || [])) {
          await fsDelete(`families/${familyData.id}/${sub}/${d.id}`);
        }
      }
      await fsDelete(`families/${familyData.id}/config/appConfig`);
      await fsDelete(`families/${familyData.id}`);
    }
    await handleSignOut();
    setDone('nuclear');
  }

  async function executeSubset() {
    const { familyData, setFamilyData } = useAppCtx;
    if (familyData?.id) {
      const SUB_MAP = {
        sitters:       ['sitters'],
        children:      ['children'],
        care_partners: ['carePartners'],
        calendars:     [], // handled via config update
        config:        [],
        sessions:      ['sessions'],
      };
      for (const sel of subsetSelected) {
        for (const sub of (SUB_MAP[sel] || [])) {
          const docs = await fsGetCol(`families/${familyData.id}/${sub}`);
          for (const d of (docs || [])) {
            await fsDelete(`families/${familyData.id}/${sub}/${d.id}`);
          }
        }
        if (sel === 'config' || sel === 'calendars') {
          await fsDelete(`families/${familyData.id}/config/appConfig`);
        }
      }
      // Refresh family data
      const updated = { ...familyData };
      if (subsetSelected.includes('sitters'))       updated.sitters = [];
      if (subsetSelected.includes('children'))      updated.children = [];
      if (subsetSelected.includes('care_partners')) updated.carePartners = [];
      if (subsetSelected.includes('config'))        updated.config = null;
      setFamilyData(updated);
    }
    setDone('subset');
    setMode(null);
    setSubsetSelected([]);
    setConfirm1(false);
    setConfirm2(false);
  }

  if (done === 'nuclear') {
    return (
      <div className="reset-done">
        <span className="reset-done__icon">✓</span>
        <h3>All data cleared</h3>
        <p>Grove has been fully reset. Restart the app to start over.</p>
      </div>
    );
  }

  if (done === 'subset') {
    return (
      <div className="reset-done">
        <span className="reset-done__icon">✓</span>
        <h3>Selected data removed</h3>
        <p>The items you selected have been cleared.</p>
        <button className="btn btn--secondary" onClick={() => setDone(null)}>Done</button>
      </div>
    );
  }

  return (
    <div className="reset-panel">
      <div className="reset-panel__intro">
        <p>Use this to clear data from Grove. <strong>This cannot be undone.</strong> The changelog is always preserved.</p>
      </div>

      {/* Maintenance — non-destructive */}
      {!mode && (
        <div style={{ marginBottom: 16 }}>
          {publishErr && <div className="settings-msg" style={{ marginBottom: 8 }}>{publishErr}</div>}
          <button className="btn btn--secondary" disabled={publishing} onClick={handlePublishNow}>
            {publishing ? 'Publishing…' : '📤 Publish schedule to caregivers now'}
          </button>
          <p className="settings-note" style={{ marginTop: 6 }}>
            {publishedAt && `Shared with caregivers at ${publishedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. `}
            Grove auto-publishes changes within ~90 seconds of any schedule edit — use this to force an immediate refresh instead of waiting.
          </p>

          {migrateMsg && <div className="settings-msg" style={{ marginTop: 12, marginBottom: 8 }}>{migrateMsg}</div>}
          <button className="btn btn--secondary" disabled={migrating} onClick={migrateSensitiveFields} style={{ marginTop: migrateMsg ? 0 : 12 }}>
            {migrating ? 'Moving…' : '🔐 Move sensitive fields to private storage'}
          </button>
          <p className="settings-note" style={{ marginTop: 6 }}>
            Moves alarm/gate codes and kids' insurance IDs out of shared docs into owner-only storage. Safe to run more than once.
          </p>

          {standardizeMsg && <div className="settings-msg" style={{ marginTop: 12, marginBottom: 8 }}>{standardizeMsg}</div>}
          <button className="btn btn--secondary" disabled={standardizing} onClick={standardizeAllTitles} style={{ marginTop: standardizeMsg ? 0 : 12 }}>
            {standardizing ? 'Standardizing…' : '🧹 Standardize all titles'}
          </button>
          <p className="settings-note" style={{ marginTop: 6 }}>
            Cleans up recurring event titles across the next year, not just what Recurring Events shows for the next 60 days. Safe to run more than once.
          </p>

          {migrateEmailsMsg && <div className="settings-msg" style={{ marginTop: 12, marginBottom: 8 }}>{migrateEmailsMsg}</div>}
          <button className="btn btn--secondary" disabled={migratingEmails} onClick={migrateSignInEmails} style={{ marginTop: migrateEmailsMsg ? 0 : 12 }}>
            {migratingEmails ? 'Migrating…' : '🌾 Migrate legacy guest/sitter emails to caregivers'}
          </button>
          <p className="settings-note" style={{ marginTop: 6 }}>
            Links old email-invite access to a caregiver profile — creating one if needed — so Google sign-in keeps working under the new per-caregiver access model. Safe to run more than once.
          </p>
        </div>
      )}

      {/* Option cards */}
      {!mode && (
        <div className="reset-options">
          <button className="reset-option reset-option--subset" onClick={() => setMode('subset')}>
            <span className="reset-option__icon">🗂</span>
            <div>
              <span className="reset-option__label">Remove specific data</span>
              <span className="reset-option__desc">Clear sitters, children, configs, or other specific categories</span>
            </div>
            <span className="reset-option__arrow">›</span>
          </button>

          <button className="reset-option reset-option--nuclear" onClick={() => setMode('nuclear')}>
            <span className="reset-option__icon">☢️</span>
            <div>
              <span className="reset-option__label">Nuclear reset</span>
              <span className="reset-option__desc">Wipe everything — all family data, settings, and sign you out</span>
            </div>
            <span className="reset-option__arrow">›</span>
          </button>
        </div>
      )}

      {/* Subset selection */}
      {mode === 'subset' && (
        <div className="reset-subset">
          <button className="btn btn--back" onClick={() => { setMode(null); setSubsetSelected([]); }}>← Back</button>
          <h3>What would you like to remove?</h3>
          <div className="reset-subset__list">
            {SUBSET_OPTIONS.map(opt => (
              <button
                key={opt.id}
                className={`reset-subset__item ${subsetSelected.includes(opt.id) ? 'selected' : ''}`}
                onClick={() => toggleSubset(opt.id)}
              >
                <div>
                  <span className="reset-subset__item-label">{opt.label}</span>
                  <span className="reset-subset__item-desc">{opt.desc}</span>
                </div>
                <span className="reset-subset__check">{subsetSelected.includes(opt.id) ? '✓' : ''}</span>
              </button>
            ))}
          </div>

          {subsetSelected.length > 0 && !confirm1 && (
            <button className="btn btn--danger btn--full" style={{ marginTop: 16 }} onClick={() => setConfirm1(true)}>
              Remove {subsetSelected.length} item{subsetSelected.length !== 1 ? 's' : ''}
            </button>
          )}

          {confirm1 && (
            <div className="reset-confirm-box">
              <p>Are you sure? This will permanently delete:</p>
              <ul>
                {subsetSelected.map(id => (
                  <li key={id}>{SUBSET_OPTIONS.find(o => o.id === id)?.label}</li>
                ))}
              </ul>
              <div className="reset-confirm-box__actions">
                <button className="btn btn--ghost" onClick={() => setConfirm1(false)}>Cancel</button>
                <button className="btn btn--danger" onClick={executeSubset}>Yes, remove these</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nuclear confirmation — two steps */}
      {mode === 'nuclear' && (
        <div className="reset-nuclear">
          <button className="btn btn--back" onClick={() => { setMode(null); setConfirm1(false); setConfirm2(false); setNuclearText(''); }}>← Back</button>
          <div className="reset-nuclear__warning">
            <span className="reset-nuclear__icon">☢️</span>
            <h3>Nuclear Reset</h3>
            <p>This will permanently delete <strong>all family data</strong> including Care Partners, children, sitters, sessions, calendars, settings, and sign you out. The changelog will also be cleared.</p>
            <p><strong>This cannot be undone.</strong></p>
          </div>

          {!confirm1 && (
            <button className="btn btn--danger btn--full" onClick={() => setConfirm1(true)}>
              I understand — continue
            </button>
          )}

          {confirm1 && !confirm2 && (
            <div className="reset-confirm-box reset-confirm-box--nuclear">
              <p>Second confirmation — type <strong>RESET</strong> to continue:</p>
              <input
                className="input"
                placeholder="Type RESET"
                value={nuclearText}
                onChange={e => setNuclearText(e.target.value.toUpperCase())}
              />
              <div className="reset-confirm-box__actions">
                <button className="btn btn--ghost" onClick={() => { setConfirm1(false); setNuclearText(''); }}>Cancel</button>
                <button
                  className="btn btn--danger"
                  disabled={nuclearText !== 'RESET'}
                  onClick={() => setConfirm2(true)}
                >
                  Confirm
                </button>
              </div>
            </div>
          )}

          {confirm2 && (
            <div className="reset-confirm-box reset-confirm-box--nuclear">
              <p>Last chance — are you absolutely sure?</p>
              <div className="reset-confirm-box__actions">
                <button className="btn btn--ghost" onClick={() => { setConfirm1(false); setConfirm2(false); setNuclearText(''); }}>
                  No, go back
                </button>
                <button className="btn btn--danger" onClick={executeNuclear}>
                  ☢️ Wipe Everything
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ACCESS MANAGEMENT ────────────────────────────────────────────────────────
// Manage who can access the app and with what role.
// guest  = read-only: schedule + kids health info (grandparents, trusted family)
// sitter = lean sitter view: their sessions, kids info, emergency contacts
